import React, { useState, useEffect } from 'react';
import { supabase } from '../../../../supabaseClient';
import { showNotification, showConfirmation } from '../../../Notification';
import AccessibleSelect from '../../../common/AccessibleSelect';
import ResponsiveDataView from '../../../common/ResponsiveDataView';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Tooltip
} from '@mui/material';
import {
  CloudUpload,
  Add,
  Search,
  Edit,
  Delete,
  PersonAdd,
  Clear,
  Person,
  CheckCircle,
  Cancel
} from '@mui/icons-material';

const UserManagement = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // add, edit
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [signatureFile, setSignatureFile] = useState(null);
  
  const [userForm, setUserForm] = useState({
    firstname: '',
    lastname: '',
    password: '',
    department: '',
    position: '',
    role: '',
    email: '',
    status: 'active',
    signature: '',
    signature_url: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('datecreated', { ascending: false });
      
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      showNotification('Error loading users: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('lgu_departments')
        .select('id, department_name, department_code')
        .eq('is_active', true)
        .order('department_name', { ascending: true });
      
      if (error) throw error;
      setDepartments(data || []);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const openModal = (type, userData = null) => {
    setModalType(type);
    setSignatureFile(null); // Reset file selection
    
    if (type === 'edit' && userData) {
      setSelectedUser(userData);
      setUserForm({
        firstname: userData.firstname || '',
        lastname: userData.lastname || '',
        password: '',
        department: userData.department || '',
        position: userData.position || '',
        role: userData.role || '',
        email: userData.email || '',
        status: userData.status || 'active',
        signature: userData.signature || '',
        signature_url: userData.signature_url || ''
      });
    } else {
      setUserForm({
        firstname: '',
        lastname: '',
        password: '',
        department: '',
        position: '',
        role: '',
        email: '',
        status: 'active',
        signature: '',
        signature_url: ''
      });
    }
    setShowModal(true);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSignatureFile(file);
    }
  };

  const uploadSignature = async (userId, file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}_${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('signatures')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('signatures')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading signature:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let signatureUrl = userForm.signature_url;
      const currentUserId = modalType === 'add' ? crypto.randomUUID() : selectedUser.userid;

      // Handle file upload if a new file is selected
      if (signatureFile) {
        signatureUrl = await uploadSignature(currentUserId, signatureFile);
      }

      if (modalType === 'add') {
        const { error } = await supabase
          .from('users')
          .insert([{
            userid: currentUserId,
            ...userForm,
            signature_url: signatureUrl,
            datecreated: new Date().toISOString()
          }]);
        
        if (error) throw error;
        showNotification('User added successfully!', 'success');
      } else if (modalType === 'edit') {
        const updateData = { 
          ...userForm,
          signature_url: signatureUrl
        };
        if (!updateData.password) {
          delete updateData.password;
        }

        const { error } = await supabase
          .from('users')
          .update(updateData)
          .eq('userid', selectedUser.userid);
        
        if (error) throw error;
        showNotification('User updated successfully!', 'success');
      }

      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      showNotification('Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    const confirmed = await showConfirmation('Are you sure you want to delete this user?');
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('users')
        .delete()
        .eq('userid', userId);
      
      if (error) throw error;
      showNotification('User deleted successfully!', 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Error: ' + error.message, 'error');
    }
  };

  const filteredUsers = users.filter(u =>
    u.firstname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.lastname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person /> User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => openModal('add')}
        >
          Add User
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search users by name, email, department, position, or role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
          size="small"
        />
      </Paper>

      {/* User Directory Responsive Data View */}
      <ResponsiveDataView
        columns={[
          {
            key: 'name',
            label: 'Name',
            priority: 'high',
            render: (u) => (
              <Typography variant="subtitle2" fontWeight="bold">
                {u.firstname} {u.lastname}
              </Typography>
            )
          },
          {
            key: 'email',
            label: 'Email',
            priority: 'high',
            render: (u) => u.email || '—'
          },
          {
            key: 'department',
            label: 'Department',
            priority: 'medium',
            render: (u) => u.department || '—'
          },
          {
            key: 'position',
            label: 'Position',
            priority: 'low',
            render: (u) => u.position || '—'
          },
          {
            key: 'role',
            label: 'Role',
            priority: 'high',
            render: (u) =>
              u.role ? (
                <Chip
                  label={u.role.toUpperCase()}
                  size="small"
                  color={u.role === 'superadmin' ? 'secondary' : u.role === 'admin' ? 'primary' : 'default'}
                  variant="outlined"
                />
              ) : (
                '—'
              )
          },
          {
            key: 'status',
            label: 'Status',
            priority: 'high',
            render: (u) => (
              <Chip
                label={u.status === 'active' ? 'Active' : 'Inactive'}
                size="small"
                color={u.status === 'active' ? 'success' : 'error'}
                icon={u.status === 'active' ? <CheckCircle /> : <Cancel />}
              />
            )
          },
          {
            key: 'actions',
            label: 'Actions',
            priority: 'high',
            align: 'right',
            render: (u) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Edit">
                  <IconButton color="primary" size="small" onClick={() => openModal('edit', u)}>
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton color="error" size="small" onClick={() => handleDelete(u.userid)}>
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Stack>
            )
          }
        ]}
        data={filteredUsers}
        loading={loading}
        loadingMessage="Loading municipal user accounts..."
        emptyMessage={searchQuery ? 'No matching users found' : 'No users found'}
        rowKey="userid"
      />

      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {modalType === 'add' ? 'Add New User' : 'Edit User'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={userForm.firstname}
                  onChange={(e) => setUserForm({ ...userForm, firstname: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={userForm.lastname}
                  onChange={(e) => setUserForm({ ...userForm, lastname: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="user@example.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  required={modalType === 'add'}
                  placeholder={modalType === 'edit' ? 'Leave blank to keep current' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AccessibleSelect
                  label="Department"
                  value={userForm.department}
                  onChange={(e) => setUserForm({ ...userForm, department: e.target.value })}
                  options={departments.map(d => ({ value: d.department_name, label: d.department_name }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Position"
                  value={userForm.position}
                  onChange={(e) => setUserForm({ ...userForm, position: e.target.value })}
                  placeholder="e.g., Cashier, Clerk"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AccessibleSelect
                  label="Role"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  options={[
                    { value: 'user', label: 'User' },
                    { value: 'admin', label: 'Admin' },
                    { value: 'superadmin', label: 'Super Admin' }
                  ]}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <AccessibleSelect
                  label="Status"
                  value={userForm.status}
                  onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' }
                  ]}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ border: '1px solid #ccc', borderRadius: 1, p: 2 }}>
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    Digital Signature (Optional)
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUpload />}
                      size="small"
                    >
                      Upload Signature
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={handleFileUpload}
                      />
                    </Button>
                    
                    {signatureFile ? (
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                        {signatureFile.name}
                      </Typography>
                    ) : userForm.signature_url ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="success.main">
                          Current signature uploaded
                        </Typography>
                        <a href={userForm.signature_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                          <Typography variant="caption" color="primary">View</Typography>
                        </a>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        No signature uploaded
                      </Typography>
                    )}
                  </Box>

                  {/* Optional: Fallback text field if they still want to use text */}
                  {/* <TextField
                    fullWidth
                    label="Or Text Signature"
                    value={userForm.signature}
                    onChange={(e) => setUserForm({ ...userForm, signature: e.target.value })}
                    placeholder="Initials"
                    margin="dense"
                    size="small"
                  /> */}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Saving...' : modalType === 'add' ? 'Add User' : 'Update User'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
